import type { TutorialAction } from '@/feature/tutorialAgent/tutorialAgentTypes'

type AliasScopeResolver = (params: { targetId: string; scope: string }) => string | null
type AliasScopeRegistrar = (params: { sourceId: string; resolvedId: string; scope: string }) => void
type ParentNodeLike = Document | Element

export type TutorialTargetResolveStrategy =
  | 'exact'
  | 'alias'
  | 'route_label_role'
  | 'container'
  | null

export type TutorialTargetResolveResult = {
  element: HTMLElement | null
  strategy: TutorialTargetResolveStrategy
}

const normalizeToken = (value: string) => value.trim().toLowerCase()

const getMetaString = (action: TutorialAction | null, key: string) => {
  if (!action?.meta) return null
  const value = action.meta[key]
  return typeof value === 'string' ? value : null
}

const escapeForAttributeSelector = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

const findElementByTutorialIdFallback = (targetId: string, root?: ParentNodeLike) => {
  const candidates = Array.from(
    (root ?? document).querySelectorAll<HTMLElement>('[data-tutorial-id]')
  )
  return candidates.find(element => element.dataset.tutorialId === targetId) ?? null
}

const resolveElementByTutorialId = (targetId: string, root?: ParentNodeLike) => {
  try {
    const escapedId = escapeForAttributeSelector(targetId)
    const exactMatch = (root ?? document).querySelector<HTMLElement>(
      `[data-tutorial-id="${escapedId}"]`
    )
    if (exactMatch) return exactMatch
  } catch {
    // fall through to robust dataset scan when selector parsing fails
  }
  return findElementByTutorialIdFallback(targetId, root)
}

const findElementByLabelAndRole = (label: string, role: string | null, root?: ParentNodeLike) => {
  const normalizedLabel = normalizeToken(label)
  const candidates = Array.from(
    (root ?? document).querySelectorAll<HTMLElement>('[data-tutorial-label]')
  ).filter(element => normalizeToken(element.dataset.tutorialLabel ?? '') === normalizedLabel)
  if (candidates.length === 0) return null

  const roleFiltered = role
    ? candidates.filter(element => {
        const elementRole =
          element.dataset.tutorialRole ??
          element.getAttribute('role') ??
          element.tagName.toLowerCase()
        return normalizeToken(elementRole) === normalizeToken(role)
      })
    : candidates

  const ordered = roleFiltered.length > 0 ? roleFiltered : candidates
  return (
    ordered.find(element => {
      const rect = element.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0
    }) ??
    ordered[0] ??
    null
  )
}

const getAliasScopes = (currentRoutePath: string, currentRouteName?: string | null) => {
  const scopes = [currentRoutePath]
  if (currentRouteName) {
    scopes.push(currentRouteName)
  }
  return scopes
}

const persistResolvedAlias = (params: {
  sourceId: string
  resolvedId: string
  scopes: string[]
  registerAlias: AliasScopeRegistrar
}) => {
  if (!params.sourceId || !params.resolvedId || params.sourceId === params.resolvedId) return
  params.scopes.forEach(scope => {
    params.registerAlias({
      sourceId: params.sourceId,
      resolvedId: params.resolvedId,
      scope
    })
  })
}

const resolveElementByAlias = (params: {
  targetId: string
  scopes: string[]
  root?: ParentNodeLike
  resolveAlias: AliasScopeResolver
  registerAlias: AliasScopeRegistrar
}) => {
  for (const scope of params.scopes) {
    const resolvedId = params.resolveAlias({ targetId: params.targetId, scope })
    if (!resolvedId) continue
    const element = resolveElementByTutorialId(resolvedId, params.root)
    if (!element) continue
    persistResolvedAlias({
      sourceId: params.targetId,
      resolvedId,
      scopes: params.scopes,
      registerAlias: params.registerAlias
    })
    return element
  }
  return null
}

export const tutorialActionHasTargetHints = (action: TutorialAction | null) => {
  if (!action) return false
  if (action.targetId) return true
  if (getMetaString(action, 'label')) return true
  if (getMetaString(action, 'containerId')) return true
  return false
}

export const resolveTutorialActionElement = (params: {
  action: TutorialAction | null
  currentRoutePath: string
  currentRouteName?: string | null
  root?: ParentNodeLike
  resolveAlias: AliasScopeResolver
  registerAlias: AliasScopeRegistrar
}): TutorialTargetResolveResult => {
  const action = params.action
  if (!action) return { element: null, strategy: null }

  const scopes = getAliasScopes(params.currentRoutePath, params.currentRouteName)

  if (action.targetId) {
    const exact = resolveElementByTutorialId(action.targetId, params.root)
    if (exact) return { element: exact, strategy: 'exact' }

    const viaAlias = resolveElementByAlias({
      targetId: action.targetId,
      scopes,
      root: params.root,
      resolveAlias: params.resolveAlias,
      registerAlias: params.registerAlias
    })
    if (viaAlias) return { element: viaAlias, strategy: 'alias' }
  }

  const label = getMetaString(action, 'label')
  const role = getMetaString(action, 'role')
  const routeHint = getMetaString(action, 'route')
  const routeMatches =
    !routeHint ||
    routeHint === params.currentRoutePath ||
    routeHint === (params.currentRouteName ?? null)
  if (label && routeMatches) {
    const byRouteLabelRole = findElementByLabelAndRole(label, role, params.root)
    if (byRouteLabelRole) {
      const resolvedId = byRouteLabelRole.dataset.tutorialId
      if (action.targetId && resolvedId) {
        persistResolvedAlias({
          sourceId: action.targetId,
          resolvedId,
          scopes,
          registerAlias: params.registerAlias
        })
      }
      return { element: byRouteLabelRole, strategy: 'route_label_role' }
    }
  }

  const containerId = getMetaString(action, 'containerId')
  if (!containerId) return { element: null, strategy: null }
  const container =
    resolveElementByTutorialId(containerId, params.root) ??
    resolveElementByAlias({
      targetId: containerId,
      scopes,
      root: params.root,
      resolveAlias: params.resolveAlias,
      registerAlias: params.registerAlias
    })
  if (!container) return { element: null, strategy: null }

  if (label) {
    const byLabel = findElementByLabelAndRole(label, role, container)
    if (!byLabel) return { element: null, strategy: null }
    const resolvedId = byLabel.dataset.tutorialId
    if (action.targetId && resolvedId) {
      persistResolvedAlias({
        sourceId: action.targetId,
        resolvedId,
        scopes,
        registerAlias: params.registerAlias
      })
    }
    return { element: byLabel, strategy: 'container' }
  }

  const idSuffix = action.targetId?.split('.').pop()
  if (!idSuffix) return { element: null, strategy: null }
  const candidates = Array.from(container.querySelectorAll<HTMLElement>('[data-tutorial-id]'))
  const bySuffix =
    candidates.find(element =>
      (element.dataset.tutorialId ?? '').toLowerCase().endsWith(idSuffix.toLowerCase())
    ) ?? null
  if (!bySuffix) return { element: null, strategy: null }
  const resolvedId = bySuffix.dataset.tutorialId
  if (action.targetId && resolvedId) {
    persistResolvedAlias({
      sourceId: action.targetId,
      resolvedId,
      scopes,
      registerAlias: params.registerAlias
    })
  }
  return { element: bySuffix, strategy: 'container' }
}
